import axios, { AxiosInstance, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import config from "../config/env";
import { tokenRefreshService } from "./tokenRefreshService";

// Enums for data types
export enum BugyoDataType {
  ESTIMATE_DATA = "ESTIMATE_DATA",
  ORDER_DATA = "ORDER_DATA",
  CUSTOMER_DATA = "CUSTOMER_DATA",
}

export const BugyoDataTypeLabels = {
  [BugyoDataType.ESTIMATE_DATA]: "見積書データ登録",
  [BugyoDataType.ORDER_DATA]: "受注伝票データ登録",
  [BugyoDataType.CUSTOMER_DATA]: "得意先データ登録",
} as const;

// Enum for field types
export enum FieldType {
  STRING = "STRING",
  INT = "INT",
  DATE = "DATE",
}

// Enum for mapping field names - used to avoid hardcoded strings when referencing mapping field types
export enum MappingFieldName {
  COLLABO_FIELD = "collaboField",
  BUGYO_FIELD = "bugyoField",
}

// Types for mapping registration
export interface MappingRegistration {
  id: number;
  collaboFormName: string;
  collaboFormId: number;
  bugyoDataType: string;
  webhookToken?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CollaboDataType {
  id: number;
  name: string;
  isUsed: boolean;
}

export interface CollaboDataField {
  type: string;
  convertType: FieldType;
  name: string;
  key: string; // Add collaboFieldKey
}

export interface CollaboTable {
  key: string;
  name: string;
  fields: CollaboDataField[];
}

export interface CollaboFieldsResponse {
  fields: CollaboDataField[];
  tables: CollaboTable[];
}

export interface BugyoDataField {
  id: number;
  dataType: BugyoDataType;
  dataInfo: "HEADER" | "DETAIL" | "DATA_LIST_INFO";
  fieldName: string;
  fieldType: FieldType;
  fieldCode: string;
  isRequired: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface MappingField {
  id?: number; // Optional - only present for existing records from DB
  collaboField: string;
  bugyoField: string;
  collaboFieldType: FieldType;
  bugyoFieldType: FieldType;
  isNew: boolean; // Track if this is a new row (user added)
  isModified: boolean; // Track if this row has been modified from original values
  isDeleted: boolean; // Track if this row is marked for deletion
  originalCollaboField?: string; // Track original values for comparison to detect real changes
  originalBugyoField?: string;
}

export interface MappingFieldItem {
  collaboFieldName: string;
  bugyoFieldName: string;
  fieldType: FieldType;
}

export interface HeaderMappingJson {
  mappingList: MappingFieldItem[];
}

export interface DetailsMappingJson {
  [key: string]: string;
}

export interface DataListInfoJson {
  [key: string]: string;
}

export interface FieldMappingItem {
  rowNumber: number;
  collaboFieldName: string;
  collaboFieldKey: string;
  bugyoFieldName: string;
  bugyoFieldId: number;
  fieldType: FieldType;
  dataInfo: "HEADER" | "DETAIL" | "DATA_LIST_INFO";
  tableKey?: string;
}

export interface FieldMappingUpdateItem {
  id: number;
  rowNumber: number;
  collaboFieldName: string;
  collaboFieldKey: string;
  bugyoFieldName: string;
  bugyoFieldId: number;
  fieldType: FieldType;
  dataInfo: "HEADER" | "DETAIL" | "DATA_LIST_INFO";
  tableKey?: string;
}

export interface FieldMappingResponse {
  id: number;
  mappingRegistrationId: number;
  rowNumber: number;
  collaboFieldName: string;
  collaboFieldKey: string;
  bugyoFieldName: string;
  bugyoFieldId: number;
  fieldType: FieldType;
  dataInfo: "HEADER" | "DETAIL" | "DATA_LIST_INFO";
  tableKey?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MappingRegistrationRequest {
  collaboFormName: string;
  collaboFormId: number;
  bugyoDataType: BugyoDataType;
  newFieldMappingList?: FieldMappingItem[];
  updateFieldMappingList?: FieldMappingUpdateItem[];
  deleteFieldMappingId?: number[];
}

export interface MappingRegistrationForm {
  collaboFormName: string;
  collaboFormId: number;
  bugyoDataType: string;
  webhookToken?: string;
  headerMappingJson: HeaderMappingJson;
  detailsMappingJson: DetailsMappingJson;
  dataListInfoJson: DataListInfoJson;
  fieldMappings: FieldMappingResponse[];
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  total?: number;
}

class MappingService {
  private client: AxiosInstance;

  constructor() {
    const baseURL = config.apiUrl;

    this.client = axios.create({
      baseURL: `${baseURL}`,
      timeout: 15000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      config => {
        const token = Cookies.get("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        console.error("Mapping request error:", error);
        return Promise.reject(error);
      }
    );

    // Setup automatic token refresh FIRST (before other interceptors)
    tokenRefreshService.setupAxiosInterceptor(this.client);

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => {
        // Check for API errors in successful HTTP responses (200 status with error in body)
        if (
          response.data &&
          (response.data.success === false || response.data.error)
        ) {
          // DEPRECATED: Temporarily disabled error navigation
          // navigationService.handleApiFailure();

          // Transform the response to error format
          const apiError = response.data;
          return Promise.reject({
            success: false,
            message:
              apiError.error ||
              apiError.message ||
              "サーバーエラーが発生しました",
            errors: apiError.errors || [
              {
                field: "general",
                message: apiError.message || "サーバーエラーが発生しました",
              },
            ],
          });
        }

        return response;
      },
      error => {
        console.error(
          "Mapping response error:",
          error.response?.data || error.message
        );

        // Handle timeout errors
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          toast.error(
            "リクエストがタイムアウトしました。しばらく待ってから再試行してください。"
          );
          return Promise.reject({
            success: false,
            message:
              "リクエストがタイムアウトしました。しばらく待ってから再試行してください。",
            errors: [
              {
                field: "general",
                message:
                  "ネットワーク接続が遅いか、サーバーが応答していません。",
              },
            ],
            isTimeout: true,
          });
        }

        // 401 errors will be handled by token refresh service

        // Don't handle 401 errors - let token refresh interceptor handle them
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }

        // Handle API failures with navigation callback (except 401 which is handled by token refresh)
        if (error.response?.status >= 400) {
          // Both client errors (4xx) and server errors (5xx) - try to go back to previous page
          // DEPRECATED: Temporarily disabled error navigation
          // navigationService.handleApiFailure();
        }

        if (error.response?.data) {
          const apiError = error.response.data;
          return Promise.reject({
            success: false,
            message: apiError.error || "サーバーエラーが発生しました",
            errors: apiError.errors || [
              {
                field: "general",
                message: apiError.message || "サーバーエラーが発生しました",
              },
            ],
            status: error.response?.status, // Preserve status code
            response: error.response, // Preserve response for status checking
          });
        }

        return Promise.reject({
          success: false,
          message: "ネットワークエラーが発生しました",
          errors: [
            {
              field: "general",
              message:
                "サーバーに接続できません。ネットワークを確認してください。",
            },
          ],
        });
      }
    );
  }

  /**
   * Get list of mapping registrations with pagination and search
   */
  async getMappingRegistrations(
    page: number = 1,
    perPage: number = 25,
    keyword?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<MappingRegistration[]>> {
    try {
      let url = `/mapping_regs?page=${page}&perPage=${perPage}`;
      if (keyword && keyword.trim()) {
        url += `&keyword=${encodeURIComponent(keyword.trim())}`;
      }
      if (startDate) {
        url += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&endDate=${encodeURIComponent(endDate)}`;
      }
      const response: AxiosResponse<ApiResponse<MappingRegistration[]>> =
        await this.client.get(url);
      return response.data;
    } catch (error: any) {
      console.error("Get mapping registrations error:", error);
      toast.error(error.message || "マッピング一覧の取得に失敗しました");
      throw error;
    }
  }

  /**
   * Get mapping registration by ID
   */
  async getMappingRegistration(
    id: number
  ): Promise<ApiResponse<MappingRegistrationForm>> {
    try {
      const response: AxiosResponse<ApiResponse<MappingRegistrationForm>> =
        await this.client.get(`/mapping_regs/${id}`);
      return response.data;
    } catch (error: any) {
      console.error("Get mapping registration error:", error);
      toast.error(error.message || "マッピング設定の取得に失敗しました");
      throw error;
    }
  }

  /**
   * Get collabo data types
   * @param excludeFormId Optional form ID to exclude from filtering (for edit mode)
   */
  async getCollaboDataTypes(
    excludeFormId?: number
  ): Promise<ApiResponse<CollaboDataType[]>> {
    try {
      const params = excludeFormId ? { excludeFormId: excludeFormId } : {};
      const response: AxiosResponse<ApiResponse<CollaboDataType[]>> =
        await this.client.get("/collabo/data_types", { params });
      return response.data;
    } catch (error: any) {
      console.error("Get collabo data types error:", error);
      toast.error(error.message || "Collaboデータタイプの取得に失敗しました");
      throw error;
    }
  }

  /**
   * Get collabo data fields by data type ID
   */
  async getCollaboDataFields(
    dtypeId: number
  ): Promise<ApiResponse<CollaboFieldsResponse>> {
    try {
      const response: AxiosResponse<ApiResponse<CollaboFieldsResponse>> =
        await this.client.get(`/collabo/data_fields?dtype_id=${dtypeId}`);
      return response.data;
    } catch (error: any) {
      console.error("Get collabo data fields error:", error);
      toast.error(
        error.message || "Collaboデータフィールドの取得に失敗しました"
      );
      throw error;
    }
  }

  /**
   * Get bugyo data field types
   */
  async getBugyoDataFieldTypes(): Promise<ApiResponse<string[]>> {
    try {
      const response: AxiosResponse<ApiResponse<string[]>> =
        await this.client.get("/bugyo_data_fields/types");
      return response.data;
    } catch (error: any) {
      console.error("Get bugyo data field types error:", error);
      toast.error(error.message || "Bugyoデータタイプの取得に失敗しました");
      throw error;
    }
  }

  /**
   * Get bugyo data fields by data type
   */
  async getBugyoDataFields(
    dtype: string
  ): Promise<ApiResponse<BugyoDataField[]>> {
    try {
      const response: AxiosResponse<ApiResponse<BugyoDataField[]>> =
        await this.client.get(`/bugyo_data_fields?dtype=${dtype}`);
      return response.data;
    } catch (error: any) {
      console.error("Get bugyo data fields error:", error);
      toast.error(
        error.message || "Bugyoデータフィールドの取得に失敗しました"
      );
      throw error;
    }
  }

  /**
   * Create mapping registration
   */
  async createMappingRegistration(
    formData: MappingRegistrationRequest
  ): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.client.post(
        "/mapping_regs",
        formData
      );
      toast.success("保存しました。");
      return response.data;
    } catch (error: any) {
      console.error("Create mapping registration error:", error);
      toast.error(error.message || "保存に失敗しました。");
      throw error;
    }
  }

  /**
   * Update mapping registration (using POST)
   */
  async updateMappingRegistration(
    id: number,
    formData: MappingRegistrationRequest
  ): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.client.post(
        `/mapping_regs/${id}`,
        formData
      );
      toast.success("保存しました。");
      return response.data;
    } catch (error: any) {
      console.error("Update mapping registration error:", error);
      toast.error(error.message || "保存に失敗しました。");
      throw error;
    }
  }

  /**
   * Delete mapping registration
   */
  async deleteMappingRegistration(id: number): Promise<ApiResponse<any>> {
    try {
      const response: AxiosResponse<ApiResponse<any>> =
        await this.client.delete(`/mapping_regs/${id}`);
      toast.success("削除しました。");
      return response.data;
    } catch (error: any) {
      console.error("Delete mapping registration error:", error);
      toast.error("削除に失敗しました。");
      throw error;
    }
  }

  /**
   * Regenerate webhook token for a mapping registration
   */
  async regenerateWebhookToken(
    id: number
  ): Promise<ApiResponse<{ webhookToken: string }>> {
    try {
      const response: AxiosResponse<ApiResponse<{ webhookToken: string }>> =
        await this.client.post(`/mapping_regs/${id}/webhook/regenerate`);
      toast.success("Webhook URLを再生成しました。");
      return response.data;
    } catch (error: any) {
      console.error("Regenerate webhook token error:", error);
      toast.error(
        error.message || "Webhook URLの再生成に失敗しました。"
      );
      throw error;
    }
  }
}

// Export singleton instance
export const mappingService = new MappingService();
export default mappingService;
